import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
	connectedCallback() {
		super.connectedCallback();

		const { signal } = this.#controller;
		const target = this.closest('.shopify-section, dialog');

		target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
		// The lightbox (<product-lightbox>, a descendant) dispatches a bubbling ZoomMediaSelectedEvent;
		// listen on self so the inline gallery follows the lightbox's current image.
		this.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
	}

	#controller = new AbortController();

	disconnectedCallback() {
		super.disconnectedCallback();

		this.#controller.abort();
	}

	/**
	 * Handles a variant update event by replacing the current media gallery with a new one.
	 *
	 * @param {Event} event - The variant update event.
	 */
	#handleVariantUpdate = (event) => {
		if (!(event instanceof VariantUpdateEvent)) return;
		const source = event.detail.data.html;

		if (!source) return;
		const newMediaGallery = source.querySelector('media-gallery');

		if (!newMediaGallery) return;

		this.replaceWith(newMediaGallery);
	};

	/**
	 * Handles the 'zoom-media:selected' event (bubbled up from the lightbox) — sync the inline gallery.
	 * @param {Event} event - The zoom-media:selected event.
	 */
	#handleZoomMediaSelected = async (event) => {
		if (!(event instanceof ZoomMediaSelectedEvent)) return;
		this.slideshow?.select(event.detail.index, undefined, { animate: false });
	};

	get slideshow() {
		return this.refs.slideshow;
	}

	get media() {
		return this.refs.media;
	}

	get presentation() {
		return this.dataset.presentation;
	}
}

if (!customElements.get('media-gallery')) {
	customElements.define('media-gallery', MediaGallery);
}
